import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import ConfirmDialog from '../../../components/ui/ConfirmDialog';
import { cn } from '../../../utils/cn';
import { buildMicrosoftTeamsOAuthUrl } from '../../../lib/utils/microsoftOAuth';
import { buildGoogleCalendarOAuthUrl } from '../../../lib/utils/googleOAuth';

const OAUTH_POPUP_FEATURES = 'popup=yes,width=520,height=720,left=120,top=80';
const OAUTH_POLL_MS = 2000;
const OAUTH_POLL_MAX_MS = 10 * 60 * 1000;

const isFilled = (value) => String(value ?? '').trim().length > 0;

const OAUTH_PROVIDER_META = {
  teams: {
    buildUrl: buildMicrosoftTeamsOAuthUrl,
    isConnected: (profile) =>
      isFilled(profile?.teams_refresh_token) || isFilled(profile?.teams_access_token),
    messageType: 'microsoft-oauth-complete',
    popupName: 'microsoft_teams_oauth',
  },
  google: {
    buildUrl: buildGoogleCalendarOAuthUrl,
    isConnected: (profile) =>
      isFilled(profile?.google_calendar_refresh_code) || isFilled(profile?.google_calendar_access_token),
    messageType: 'google-oauth-complete',
    popupName: 'google_calendar_oauth',
  },
};

const getOauthProviders = (t) => ({
  teams: {
    ...OAUTH_PROVIDER_META.teams,
    connectLabel: t('ai.booking.teamsConnect'),
    reconnectLabel: t('ai.booking.teamsReconnect'),
    waitingLabel: t('ai.booking.teamsWaiting'),
    pendingLabel: t('ai.booking.teamsPending'),
    connectedTitle: t('ai.booking.teamsConnectedTitle'),
    connectedDescription: t('ai.booking.teamsConnectedDesc'),
    activateHint: t('ai.booking.teamsActivateHint'),
    missingClientHint: null,
  },
  google: {
    ...OAUTH_PROVIDER_META.google,
    connectLabel: t('ai.booking.googleConnect'),
    reconnectLabel: t('ai.booking.googleReconnect'),
    waitingLabel: t('ai.booking.googleWaiting'),
    pendingLabel: t('ai.booking.googlePending'),
    connectedTitle: t('ai.booking.googleConnectedTitle'),
    connectedDescription: t('ai.booking.googleConnectedDesc'),
    activateHint: t('ai.booking.googleActivateHint'),
    missingClientHint: t('ai.booking.googleMissingClient'),
  },
});

const getBookingTypes = (t) => [
  { value: 'calendly', label: t('ai.booking.calendly'), icon: 'Calendar' },
  { value: 'teams', label: t('ai.booking.teams'), icon: 'Video' },
  { value: 'google', label: t('ai.booking.google'), icon: 'CalendarDays' },
];

const getSetupSteps = (t) => ({
  calendly: [
    { step: 1, title: t('ai.booking.calendlyStep1Title'), description: t('ai.booking.calendlyStep1Desc'), icon: 'UserPlus' },
    { step: 2, title: t('ai.booking.calendlyStep2Title'), description: t('ai.booking.calendlyStep2Desc'), icon: 'Key' },
    { step: 3, title: t('ai.booking.calendlyStep3Title'), description: t('ai.booking.calendlyStep3Desc'), icon: 'Link' },
  ],
  google: [
    { step: 1, title: t('ai.booking.googleStep1Title'), description: t('ai.booking.googleStep1Desc'), icon: 'Link' },
    { step: 2, title: t('ai.booking.googleStep2Title'), description: t('ai.booking.googleStep2Desc'), icon: 'Shield' },
    { step: 3, title: t('ai.booking.googleStep3Title'), description: t('ai.booking.googleStep3Desc'), icon: 'Zap' },
  ],
  teams: [
    { step: 1, title: t('ai.booking.teamsStep1Title'), description: t('ai.booking.teamsStep1Desc'), icon: 'Link' },
    { step: 2, title: t('ai.booking.teamsStep2Title'), description: t('ai.booking.teamsStep2Desc'), icon: 'Shield' },
    { step: 3, title: t('ai.booking.teamsStep3Title'), description: t('ai.booking.teamsStep3Desc'), icon: 'Zap' },
  ],
});

const EXTERNAL_LINKS = {
  calendly: 'https://calendly.com',
  google: 'https://calendar.google.com',
  teams: 'https://www.microsoft.com/microsoft-teams',
};

const emptyForm = (type) => {
  if (type === 'calendly') return { apiKey: '' };
  return {};
};

const formFromProfile = (profile, type) => {
  if (!profile) return emptyForm(type);
  if (type === 'calendly') {
    return { apiKey: profile.calendly_api_key ?? '' };
  }
  return {};
};

export const isTeamsConnected = (profile) => OAUTH_PROVIDER_META.teams.isConnected(profile);

export const isGoogleConnected = (profile) => OAUTH_PROVIDER_META.google.isConnected(profile);

export const hasStoredBookingCredentials = (profile, bookingType) => {
  if (!profile) return false;
  if (bookingType === 'calendly') return isFilled(profile.calendly_api_key);
  if (bookingType === 'google') return isGoogleConnected(profile);
  if (bookingType === 'teams') return isTeamsConnected(profile);
  return false;
};

export const hasBookingFormValues = (bookingType, form) => {
  if (bookingType === 'calendly') return isFilled(form.apiKey);
  return false;
};

export const isBookingFormComplete = (bookingType, form) => {
  if (bookingType === 'calendly') {
    return isFilled(form.apiKey);
  }
  return false;
};

export const isBookingProviderReady = (bookingType, form, profile) => {
  if (bookingType === 'teams') return isTeamsConnected(profile);
  if (bookingType === 'google') return isGoogleConnected(profile);
  return isBookingFormComplete(bookingType, form);
};

const buildSavePayload = (bookingType, form) => {
  if (bookingType === 'calendly') {
    return { bookingType, calendly: { apiKey: form.apiKey } };
  }
  return { bookingType };
};

const CalendlyIntegration = ({
  profile,
  onSave,
  onActivate,
  onClear,
  onRefetch,
  onOAuthSuccess,
  isSaving = false,
  isActivating = false,
  isClearing = false,
}) => {
  const { t } = useTranslation();
  const bookingTypes = useMemo(() => getBookingTypes(t), [t]);
  const oauthProviders = useMemo(() => getOauthProviders(t), [t]);
  const setupStepsByType = useMemo(() => getSetupSteps(t), [t]);
  const providerLabel = useCallback(
    (type) => bookingTypes.find((item) => item.value === type)?.label ?? type,
    [bookingTypes],
  );

  const activeProvider = profile?.booking_type || null;
  const [bookingType, setBookingType] = useState(activeProvider || 'calendly');
  const [form, setForm] = useState(() => formFromProfile(profile, activeProvider || 'calendly'));
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [oauthPending, setOauthPending] = useState(null);
  const oauthPopupRef = useRef(null);
  const pollRef = useRef(null);
  const oauthPollStartedAtRef = useRef(null);
  const oauthFinishedRef = useRef(false);

  const stopOAuthPoll = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    setOauthPending(null);
  }, []);

  // Refresh current tab's fields when profile is refetched (e.g. after Save)
  useEffect(() => {
    setForm(formFromProfile(profile, bookingType));
  }, [
    profile,
    bookingType,
    profile?.calendly_api_key,
    profile?.google_calendar_access_token,
    profile?.google_calendar_client_id,
    profile?.google_calendar_refresh_code,
    profile?.google_calendar_client_secret,
    profile?.teams_access_token,
    profile?.teams_client_id,
    profile?.teams_client_secret,
    profile?.teams_refresh_token,
  ]);

  const handleTypeChange = (type) => {
    setBookingType(type);
    setForm(formFromProfile(profile, type));
  };

  const updateField = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const userProfileId = profile?.id;
  const oauthConfig = oauthProviders[bookingType];
  const oauthConnected = oauthConfig?.isConnected(profile) ?? false;
  const oauthUrl = oauthConfig?.buildUrl(userProfileId) ?? null;
  const formComplete = isBookingProviderReady(bookingType, form, profile);
  const isActive = activeProvider === bookingType;
  const canActivate = formComplete && !isActive;
  const showSaveButton = bookingType === 'calendly';
  const isOAuthProvider = Boolean(oauthConfig);
  const canClear =
    hasStoredBookingCredentials(profile, bookingType) || hasBookingFormValues(bookingType, form);
  const busy = isSaving || isActivating || isClearing || Boolean(oauthPending);

  const clearConfirmMessage = isActive
    ? t('ai.booking.clearConfirmActive', { provider: providerLabel(bookingType) })
    : t('ai.booking.clearConfirm', { provider: providerLabel(bookingType) });

  const handleClearConfirm = () => {
    setShowClearConfirm(false);
    onClear(bookingType);
  };

  const finishOAuth = useCallback((providerOverride) => {
    const provider = providerOverride ?? oauthPending;
    if (!provider || oauthFinishedRef.current) return;
    oauthFinishedRef.current = true;

    try {
      oauthPopupRef.current?.close();
    } catch {
      // ignore
    }
    oauthPopupRef.current = null;
    stopOAuthPoll();
    onOAuthSuccess?.(provider);
  }, [oauthPending, stopOAuthPoll, onOAuthSuccess]);

  const handleConnectOAuth = (provider) => {
    const config = oauthProviders[provider];
    const url = config?.buildUrl(userProfileId);
    if (!url) return;
    oauthFinishedRef.current = false;
    stopOAuthPoll();

    const popup = window.open(url, config.popupName, OAUTH_POPUP_FEATURES);
    if (!popup) {
      window.alert(
        t('ai.booking.allowPopups', { provider: providerLabel(provider) }),
      );
      return;
    }

    oauthPopupRef.current = popup;
    setOauthPending(provider);
    oauthPollStartedAtRef.current = Date.now();

    // Do not read popup.closed — Google/Microsoft set COOP headers and the browser
    // blocks cross-origin access (console warning). Completion uses postMessage from
    // /integrations/*/callback and profile refetch below.
    pollRef.current = setInterval(async () => {
      if (
        oauthPollStartedAtRef.current
        && Date.now() - oauthPollStartedAtRef.current > OAUTH_POLL_MAX_MS
      ) {
        stopOAuthPoll();
        oauthPopupRef.current = null;
        return;
      }
      await onRefetch?.();
    }, OAUTH_POLL_MS);
  };

  useEffect(() => {
    if (!oauthPending) return;
    const config = oauthProviders[oauthPending];
    if (config?.isConnected(profile)) {
      finishOAuth();
    }
  }, [
    oauthPending,
    oauthProviders,
    profile?.teams_access_token,
    profile?.teams_refresh_token,
    profile?.google_calendar_access_token,
    profile?.google_calendar_refresh_code,
    profile,
    finishOAuth,
  ]);

  useEffect(() => {
    const onMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      const provider = Object.keys(OAUTH_PROVIDER_META).find(
        (key) => OAUTH_PROVIDER_META[key].messageType === event.data?.type,
      );
      if (!provider) return;
      onRefetch?.().then(() => finishOAuth(provider));
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [onRefetch, finishOAuth]);

  // Refetch when user returns to this tab (e.g. after OAuth redirect in popup).
  useEffect(() => {
    if (!oauthPending) return;
    const onFocus = () => {
      onRefetch?.();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [oauthPending, onRefetch]);

  useEffect(() => () => stopOAuthPoll(), [stopOAuthPoll]);

  const setupSteps = setupStepsByType[bookingType] ?? setupStepsByType.calendly;
  const visitLabel =
    bookingType === 'calendly'
      ? t('ai.booking.visitCalendly')
      : bookingType === 'google'
        ? t('ai.booking.visitGoogle')
        : t('ai.booking.visitTeams');

  return (
    <>
    <div className="bg-card border border-border rounded-xl p-4 md:p-6 lg:p-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
            <Icon name="Calendar" size={20} color="var(--color-success)" />
          </div>
          <div>
            <h2 className="text-lg md:text-xl font-semibold text-foreground">{t('ai.booking.title')}</h2>
            <p className="text-sm text-muted-foreground">
              {t('ai.booking.subtitle')}
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-6">
        <div className="space-y-3">
          <label className="block text-sm font-medium text-foreground">{t('ai.booking.provider')}</label>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6">
            {bookingTypes.map((type) => {
              const selected = bookingType === type.value;
              const isProviderActive = activeProvider === type.value;
              return (
                <button
                  key={type.value}
                  type="button"
                  onClick={() => handleTypeChange(type.value)}
                  className={cn(
                    'relative flex items-center gap-3 md:gap-4 rounded-xl border-2 p-4 md:p-5 text-left',
                    'transition-all duration-250 cursor-pointer',
                    isProviderActive
                      ? 'bg-blue-100 border-primary shadow-elevation-md hover:bg-blue-200/80'
                      : cn(
                          'bg-card hover:shadow-elevation-md',
                          selected ? 'border-primary shadow-elevation-md' : 'border-border',
                        ),
                  )}
                >
                  {selected && (
                    <div className="absolute top-3 right-3 w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                      <Icon name="Check" size={14} color="#FFFFFF" />
                    </div>
                  )}
                  <div
                    className={cn(
                      'w-10 h-10 md:w-12 md:h-12 rounded-lg flex items-center justify-center flex-shrink-0',
                      isProviderActive || selected
                        ? 'bg-gradient-to-br from-primary to-accent'
                        : 'bg-muted',
                    )}
                  >
                    <Icon
                      name={type.icon}
                      size={20}
                      color={isProviderActive || selected ? '#FFFFFF' : 'var(--color-muted-foreground)'}
                    />
                  </div>
                  <div className="flex-1 min-w-0 pr-6">
                    <span className="text-sm md:text-base font-semibold text-foreground block truncate">
                      {type.label}
                    </span>
                    {isProviderActive && (
                      <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary mt-1">
                        <Icon name="CheckCircle2" size={14} />
                        {t('ai.booking.active')}
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="bg-muted/50 rounded-lg p-4">
          <h3 className="text-sm font-medium text-foreground mb-3">{t('ai.booking.setupGuide')}</h3>
          <div className="space-y-4">
            {setupSteps.map((step) => (
              <div key={step.step} className="flex gap-3">
                <div className="w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center flex-shrink-0 text-sm font-semibold">
                  {step.step}
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-foreground mb-1">{step.title}</h4>
                  <p className="text-xs text-muted-foreground">{step.description}</p>
                </div>
                <Icon name={step.icon} size={20} color="var(--color-muted-foreground)" className="flex-shrink-0" />
              </div>
            ))}
          </div>
        </div>

        {bookingType === 'calendly' && (
          <Input
            label={t('ai.booking.calendlyApiKey')}
            type="text"
            value={form.apiKey}
            onChange={updateField('apiKey')}
            placeholder={t('ai.booking.calendlyApiKeyPlaceholder')}
            autoComplete="off"
          />
        )}

        {isOAuthProvider && oauthConfig && (
          <div className="space-y-4">
            {oauthConnected ? (
              <div className="rounded-lg border border-success/30 bg-success/10 p-4 flex items-start gap-3">
                <Icon name="CheckCircle2" size={20} className="text-success flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-foreground">{oauthConfig.connectedTitle}</p>
                  <p className="text-xs text-muted-foreground mt-1">{oauthConfig.connectedDescription}</p>
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                {t('ai.booking.oauthPopupHint')}
              </p>
            )}

            {oauthPending === bookingType && (
              <p className="text-sm text-foreground flex items-center gap-2">
                <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                {oauthConfig.pendingLabel}
              </p>
            )}

            <Button
              variant="default"
              iconName="Link"
              iconPosition="left"
              onClick={() => handleConnectOAuth(bookingType)}
              disabled={!oauthUrl || busy}
              className="w-full sm:w-auto"
            >
              {oauthPending === bookingType
                ? oauthConfig.waitingLabel
                : oauthConnected
                  ? oauthConfig.reconnectLabel
                  : oauthConfig.connectLabel}
            </Button>

            {!userProfileId && (
              <p className="text-xs text-destructive flex items-center gap-1.5">
                <Icon name="AlertCircle" size={14} className="flex-shrink-0" />
                {t('ai.booking.profileNotLoaded')}
              </p>
            )}

            {!oauthUrl && oauthConfig.missingClientHint && (
              <p className="text-xs text-destructive flex items-center gap-1.5">
                <Icon name="AlertCircle" size={14} className="flex-shrink-0" />
                {oauthConfig.missingClientHint}
              </p>
            )}
          </div>
        )}

        {!formComplete && (
          <p className="text-xs text-destructive flex items-center gap-1.5">
            <Icon name="AlertCircle" size={14} className="flex-shrink-0" />
            {isOAuthProvider && oauthConfig
              ? oauthConfig.activateHint
              : t('ai.booking.fillFieldsHint')}
          </p>
        )}

        {isActive && (
          <p className="text-xs text-success flex items-center gap-1.5">
            <Icon name="CheckCircle2" size={14} />
            {t('ai.booking.providerActive')}
          </p>
        )}

        <div className="flex flex-col sm:flex-row gap-3">
          {showSaveButton && (
            <Button
              variant="default"
              iconName="Save"
              iconPosition="left"
              onClick={() => onSave(buildSavePayload(bookingType, form))}
              loading={isSaving}
              disabled={busy && !isSaving}
              className="flex-1"
            >
              {isSaving ? t('ai.booking.saving') : t('common.save')}
            </Button>
          )}
          <Button
            variant="default"
            iconName="Zap"
            iconPosition="left"
            onClick={() =>
              isOAuthProvider
                ? onActivate({ bookingType })
                : onActivate(buildSavePayload(bookingType, form))
            }
            loading={isActivating}
            disabled={!canActivate || (busy && !isActivating)}
            className="flex-1"
          >
            {isActivating ? t('ai.booking.activating') : isActive ? t('ai.booking.active') : t('ai.booking.activate')}
          </Button>
          {bookingType === 'calendly' && (
            <Button
              variant="outline"
              iconName="ExternalLink"
              iconPosition="right"
              onClick={() => window.open(EXTERNAL_LINKS[bookingType], '_blank')}
              disabled={busy}
            >
              {visitLabel}
            </Button>
          )}
        </div>

        <div className="pt-2 border-t border-border">
          <Button
            variant="outline"
            iconName="Trash2"
            iconPosition="left"
            onClick={() => setShowClearConfirm(true)}
            loading={isClearing}
            disabled={!canClear || (busy && !isClearing)}
            className="w-full sm:w-auto text-destructive border-destructive/30 hover:bg-destructive/10 hover:text-destructive"
          >
            {isClearing ? t('ai.booking.clearing') : t('ai.booking.clearCredentials', { provider: providerLabel(bookingType) })}
          </Button>
          <p className="text-xs text-muted-foreground mt-2">
            {t('ai.booking.clearHint')}
          </p>
        </div>
      </div>
    </div>

    {createPortal(
      <ConfirmDialog
        isOpen={showClearConfirm}
        onClose={() => setShowClearConfirm(false)}
        onConfirm={handleClearConfirm}
        title={t('ai.booking.clearConfirmTitle', { provider: providerLabel(bookingType) })}
        message={clearConfirmMessage}
        confirmText={t('ai.booking.clearConfirmButton')}
        cancelText={t('common.cancel')}
      />,
      document.body,
    )}
    </>
  );
};

export default CalendlyIntegration;
